import React, { useState } from 'react';
import { Platform } from 'react-native';
import { Button, Checkbox, Heading, Layout, Link, Text } from '../../../../components/core';
import { getRoute, useExternalNavigate, useNavigate } from '../../../../routes';
import { getStylesheet } from '../../../../styles';

export type SurveyStartContentProps = {
  onContinue: () => void;
  surveyName: string;
  estimatedCompletionMinutes: string; // allow string just in case formatting occurs upstream
};

export const SurveyStartContent = ({
  onContinue,
  surveyName,
  estimatedCompletionMinutes,
}: SurveyStartContentProps): JSX.Element => {
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const navigate = useNavigate();
  const externalNavigate = useExternalNavigate();
  /**
   * We want to open the terms in a new tab, but the target page is authenticated so we can't use a normal external link
   * For web: open the route as an external page, which defaults to using a new tab
   * For mobile: we can't guarantee the user is logged in on their browser, so we open in-app with normal navigate behavior
   * This could be removed if: 1. we make the terms a public page at some point, or 2. we host the terms in a react modal
   */
  const onPressTermsLink: () => void = () => {
    if (Platform.OS === 'web') {
      // open in new tab
      externalNavigate(getRoute('consentSurveyAnalytics', {}));
    } else {
      // open in app browser
      navigate(getRoute('consentSurveyAnalytics', {}));
    }
  };

  return (
    <Layout.VStack space={6} flex={1}>
      <Layout.VStack space={4} flex={1}>
        <Layout.VStack space={4}>
          <Heading.h2 level={2}>{surveyName}</Heading.h2>

          <Text.caption bold>
            Estimated time to complete: {estimatedCompletionMinutes} min
          </Text.caption>

          <Layout.VStack space={3}>
            <Text.para>
              Your school has partnered with Mantra Health to deliver a survey that will help us
              understand how to better support students on your campus. At the end of the survey, we
              will provide you with a customized list of resources from your campus, local
              community, and national organizations that match with any areas you need or want help
              with.
            </Text.para>

            <Text.para>
              It's important for you to understand that{' '}
              <Text.para bold>
                your information will be stored securely and confidentially
              </Text.para>{' '}
              - only a small, select group of individuals on your campus will have access to your
              individual responses. In most instances, your data will only be accessed as part of a
              de-identified, aggregate dataset used to understand needs across the student body.
            </Text.para>

            <Text.para>
              We understand that it can feel vulnerable to share information about yourself and your
              life. We want you to know that we will keep your information private and we will use
              it to help students like you get access to better support.
            </Text.para>
          </Layout.VStack>
        </Layout.VStack>
        <Checkbox value="agreeToTerms" isChecked={agreeToTerms} onChange={setAgreeToTerms}>
          <Text.caption bold>
            I agree to the{' '}
            {/* The Link component is expecting a 'to' prop, but our custom onPress handler ignores it. */}
            <Link.caption onPress={onPressTermsLink} to="">
              Mantra Health Consent to Participate in Survey and Analytics
            </Link.caption>
          </Text.caption>
        </Checkbox>
      </Layout.VStack>

      <Layout.Box {...styles.footer}>
        <Button.primaryMedium
          maxWidth="500px"
          width="100%"
          onPress={onContinue}
          aria-label="Agree to the terms and start the survey"
          isDisabled={!agreeToTerms}
          testID="survey-start-continue-button"
        >
          Agree and Start
        </Button.primaryMedium>
      </Layout.Box>
    </Layout.VStack>
  );
};

const styles = getStylesheet({
  footer: {
    paddingX: 4,
    paddingBottom: 4,
  },
});
